import { initRankings } from './js/rankings/index.js';
import { loadingCoordinator } from './core/events/index.js';
import { GenderPreferenceModal } from './js/components/GenderPreferenceModal.js';
import { storage } from './js/utils/storage.js';
import { maybeShowGoogleOneTap } from './js/utils/googleOneTap.js';
import { isPlaceholderCharacterName, pickResolvedCharacterName } from './js/rankings/components/character-name-utils.js';

async function initRankingsPage() {
  try {
    loadingCoordinator.registerDeferredModule('rankings-post-essentials-hydration', async () => {
      const getComp = window.__getRankingsComponent;
      const rankingsComponent = typeof getComp === 'function' ? getComp() : null;
      if (!rankingsComponent?.hydrateDeferredRankingsData) return;
      await rankingsComponent.hydrateDeferredRankingsData();
    }, { priority: 2, useIdleCallback: true });

    const rankingsContainer = document.getElementById('rankings-content');
    const rankingsComponent = await initRankings(rankingsContainer);
    const firstRenderReady = await rankingsComponent?.waitForFirstRender?.();

    if (firstRenderReady && !loadingCoordinator.essentialsLoaded) {
      loadingCoordinator.markEssentialsLoaded();
    }

    const userData = storage.get('userData');
    const user = (userData && userData.id && userData.isLoggedIn) ? userData : null;

    const scheduleGoogleOneTap = () => {
      if (user) return;
      setTimeout(() => {
        maybeShowGoogleOneTap().catch((error) => {
          console.warn('[Rankings] Google One Tap failed:', error);
        });
      }, 400);
    };

    if (!user?.isLoggedIn) {
      if (GenderPreferenceModal.shouldShow()) {
        setTimeout(() => {
          const genderModal = new GenderPreferenceModal();
          let didScheduleOneTap = false;
          const scheduleOneTapOnce = () => {
            if (didScheduleOneTap) return;
            didScheduleOneTap = true;
            scheduleGoogleOneTap();
          };

          void genderModal.show((isMalePreference) => {
            const genderPreferenceEvent = new CustomEvent('genderPreferenceSelected', {
              detail: { isMale: isMalePreference, skipScroll: true }
            });
            document.dispatchEvent(genderPreferenceEvent);
            scheduleOneTapOnce();
          }).then(() => {
            if (!document.getElementById('gender-preference-modal')) {
              scheduleOneTapOnce();
              return;
            }

            const observer = new MutationObserver(() => {
              if (document.getElementById('gender-preference-modal')) return;
              observer.disconnect();
              scheduleOneTapOnce();
            });
            observer.observe(document.body, { childList: true });
          }).catch((error) => {
            console.warn('[Rankings] Gender preference modal failed:', error);
            scheduleOneTapOnce();
          });
        }, 100);
      } else {
        scheduleGoogleOneTap();
      }
    }

    setTimeout(() => {
      const footer = document.querySelector('footer');
      if (footer) {
        footer.style.visibility = 'visible';
      }
    }, 100);

    const params = new URLSearchParams(location.search);

    const waitForComponent = async (maxRetries = 10, initialDelay = 600) => {
      for (let i = 0; i < maxRetries; i++) {
        const delay = initialDelay + (i * 200);
        await new Promise(resolve => setTimeout(resolve, delay));

        const getComp = window.__getRankingsComponent || (() => rankingsComponent);
        const comp = getComp && getComp();

        if (comp && comp.characterModal) return comp;
      }

      console.warn('[DeepLink] Component not available after retries');
      return null;
    };

    const clearOpenScenarioParams = () => {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('action') !== 'open_scenario') return;
        ['action', 'character', 'scenario', 'scenario_name'].forEach((param) => {
          url.searchParams.delete(param);
        });
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        window.history.replaceState({}, '', nextUrl);
      } catch (error) {
        console.warn('[DeepLink] Failed to clear open_scenario URL params:', error);
      }
    };

    const withNavigationNameHint = (character, characterId, nameHint) => {
      const hint = typeof nameHint === 'string' ? nameHint.trim() : '';
      if (!character) {
        return hint && !isPlaceholderCharacterName(hint, characterId)
          ? { id: characterId, name: hint }
          : character;
      }
      if (pickResolvedCharacterName(character, characterId)) return character;
      if (!hint || isPlaceholderCharacterName(hint, characterId)) return character;
      return { ...character, name: hint };
    };

    const resolveCharacterForNavigation = async (comp, characterId, isPrivate = false, { forceRefresh = false, nameHint = '' } = {}) => {
      const targetId = String(characterId);
      const seedInput = isPrivate
        ? { id: characterId, private: true, rank: 'Private', ...(nameHint ? { name: nameHint } : {}) }
        : (nameHint ? { id: characterId, name: nameHint } : characterId);

      if (!forceRefresh) {
        const inState = comp?.rankingsState?.characters?.find((character) => String(character?.id) === targetId);
        if (inState) return withNavigationNameHint(inState, characterId, nameHint);
      }

      if (isPrivate && typeof comp?.service?.getUserPrivateCharacters === 'function') {
        try {
          if (forceRefresh && typeof comp?.service?.clearPrivateCharactersCache === 'function') {
            comp.service.clearPrivateCharactersCache();
          }
          const privateUserData = storage.get('userData');
          const userId = privateUserData?.id;
          if (userId) {
            const privateCharacters = await comp.service.getUserPrivateCharacters(userId);
            const privateChar = privateCharacters.find((character) => String(character?.id) === targetId);
            if (privateChar) return withNavigationNameHint(privateChar, characterId, nameHint);
          }
        } catch (error) {
          console.warn('[DeepLink] Failed to fetch private character for navigation:', error);
        }
      }

      if (typeof comp?.service?.getCharacterDetails === 'function') {
        try {
          const fetchedCharacter = await comp.service.getCharacterDetails(seedInput, { forceRefresh });
          if (fetchedCharacter?.id) return withNavigationNameHint(fetchedCharacter, characterId, nameHint);
        } catch (error) {
          console.warn('[DeepLink] Failed to fetch character details for filter sync:', error);
        }
      }

      return withNavigationNameHint(
        isPrivate
          ? { id: characterId, private: true, rank: 'Private' }
          : { id: characterId },
        characterId,
        nameHint
      );
    };

    const runOpenCharacter = async (comp, characterId, isPrivate, nameHint = '') => {
      if (!characterId) {
        console.warn('[DeepLink] Invalid character ID');
        return;
      }

      const targetCharacter = await resolveCharacterForNavigation(comp, characterId, isPrivate, {
        forceRefresh: true,
        nameHint
      });
      if (isPrivate && typeof comp?.applyCharacterNavigationFilters === 'function') {
        await comp.applyCharacterNavigationFilters({ ...targetCharacter, private: true });
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      await comp.characterModal.show(
        {
          id: characterId,
          name: targetCharacter?.name,
          private: isPrivate,
          rank: isPrivate ? 'Private' : targetCharacter?.rank
        },
        { isPrivate, forceRefresh: true }
      );
    };

    if (params.get('action') === 'open_character') {
      const characterId = parseInt(params.get('character') || '0', 10);
      const isPrivate = params.get('is_private') === 'true';
      const nameHint = params.get('character_name') || '';

      (async () => {
        try {
          const comp = await waitForComponent();
          if (!comp) return;
          await runOpenCharacter(comp, characterId, isPrivate, nameHint);
        } catch (e) {
          console.error('[DeepLink] Failed to open character:', e);
        }
      })();
    }

    const runOpenScenario = async (comp, characterId, scenarioId) => {
      if (!characterId || !scenarioId) {
        console.warn('[DeepLink] Invalid character or scenario ID');
        return;
      }
      const scenarioIdStr = String(scenarioId);

      try {
        const targetCharacter = await resolveCharacterForNavigation(comp, characterId);
        if (typeof comp?.applyCharacterNavigationFilters === 'function') {
          await comp.applyCharacterNavigationFilters(targetCharacter);
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        await comp.characterModal.show(characterId, { skipAutoOpenFirstSpot: true });
        if (typeof comp.characterModal.applyScenarioNavigationFilters === 'function') {
          comp.characterModal.applyScenarioNavigationFilters(scenarioIdStr);
        }
        await new Promise(resolve => setTimeout(resolve, 300));

        const modalRoot = comp.characterModal.element || document.getElementById('characterModal') || document;

        const findButtonInRow = (rowSelector) => {
          const row = modalRoot.querySelector(rowSelector);
          if (!row) return null;
          return row.querySelector(`.spot-button[data-spot-id="${scenarioId}"], .spot-button[data-spot-id="${scenarioIdStr}"], .conversation-button[data-spot-id="${scenarioId}"], .conversation-button[data-spot-id="${scenarioIdStr}"]`);
        };

        const isButtonVisible = (button) => {
          if (!button || !(button instanceof HTMLElement)) return false;
          const styles = window.getComputedStyle(button);
          if (styles.display === 'none' || styles.visibility === 'hidden') return false;
          return button.getClientRects().length > 0;
        };

        const findVisibleButtonInRow = (rowSelector) => {
          const row = modalRoot.querySelector(rowSelector);
          if (!row) return null;
          const buttons = Array.from(row.querySelectorAll(
            `.spot-button[data-spot-id="${scenarioId}"], .spot-button[data-spot-id="${scenarioIdStr}"], .conversation-button[data-spot-id="${scenarioId}"], .conversation-button[data-spot-id="${scenarioIdStr}"]`
          ));
          return buttons.find((button) => isButtonVisible(button)) || null;
        };

        const isScenarioSelected = () => {
          const selectedScenario = String(comp?.characterModal?.selectedScenarioId || '');
          if (selectedScenario === scenarioIdStr) return true;
          return !!modalRoot.querySelector(
            `.spot-button.selected[data-spot-id="${scenarioId}"], .spot-button.selected[data-spot-id="${scenarioIdStr}"], .conversation-button.selected[data-spot-id="${scenarioId}"], .conversation-button.selected[data-spot-id="${scenarioIdStr}"]`
          );
        };

        const setSearchBarFromButton = (button) => {
          const name = button.getAttribute('data-full-name')
            || button.querySelector('.spot-title')?.getAttribute('data-full-name')
            || button.getAttribute('aria-label')
            || button.querySelector('.spot-title')?.textContent?.trim()
            || '';
          if (!name) return;
          const searchBar = /** @type {HTMLInputElement | null} */ (
            modalRoot.querySelector('#public-spot-search, #private-spot-search, #premium-spot-search')
          );
          if (searchBar) {
            searchBar.value = name;
            searchBar.dispatchEvent(new Event('input', { bubbles: true }));
          }
        };

        const tryViewAndClick = async (view, rowSelector) => {
          if (typeof comp.characterModal.setSpotsView === 'function') {
            comp.characterModal.setSpotsView(view);
          }
          if (typeof comp.characterModal.renderPublicSpots === 'function') {
            await comp.characterModal.renderPublicSpots();
          }
          if (typeof comp.characterModal.applyScenarioNavigationFilters === 'function') {
            comp.characterModal.applyScenarioNavigationFilters(scenarioIdStr);
          }

          const initialButton = findButtonInRow(rowSelector);
          if (!initialButton) return false;

          setSearchBarFromButton(initialButton);
          if (typeof comp.characterModal.toggleSearchBar === 'function') {
            comp.characterModal.toggleSearchBar(view === 'conversations' ? 'premium' : 'public');
          }
          if (typeof comp.characterModal.applyScenarioNavigationFilters === 'function') {
            comp.characterModal.applyScenarioNavigationFilters(scenarioIdStr);
          }

          for (let attempt = 0; attempt < 4; attempt++) {
            const buttonToClick = attempt === 0
              ? (findVisibleButtonInRow(rowSelector) || initialButton)
              : (findVisibleButtonInRow(rowSelector) || findButtonInRow(rowSelector));
            if (!buttonToClick) {
              await new Promise(resolve => setTimeout(resolve, 120));
              continue;
            }

            if (attempt > 0 && comp?.characterModal) {
              comp.characterModal.lastSpotClick = 0;
            }

            buttonToClick.dispatchEvent(new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window
            }));

            await new Promise(resolve => setTimeout(resolve, 160 + (attempt * 120)));
            if (isScenarioSelected()) return true;
          }

          return false;
        };

        if (await tryViewAndClick('scenarios', '#public-spot-row')) return;
        if (await tryViewAndClick('private', '#private-scenarios-row')) return;
        if (await tryViewAndClick('conversations', '#premium-spot-row')) return;
        console.warn(`[DeepLink] Scenario button not found for scenario ${scenarioId}`);
      } catch (e) {
        console.error('[DeepLink] Failed to open scenario:', e);
      } finally {
        clearOpenScenarioParams();
      }
    };

    if (params.get('action') === 'open_scenario') {
      const characterId = parseInt(params.get('character') || '0', 10);
      const scenarioId = parseInt(params.get('scenario') || '0', 10);
      (async () => {
        const comp = await waitForComponent();
        if (comp) await runOpenScenario(comp, characterId, scenarioId);
      })();
    }

    document.addEventListener('rankingsOpenScenario', async (e) => {
      const { characterId: cId, scenarioId: sId } = (e && /** @type {CustomEvent} */ (e).detail) || {};
      const characterId = parseInt(cId || '0', 10);
      const scenarioId = parseInt(sId || '0', 10);
      const comp = await waitForComponent();
      if (comp && characterId && scenarioId) await runOpenScenario(comp, characterId, scenarioId);
    });

    document.addEventListener('rankingsOpenCharacter', async (e) => {
      const detail = (e && /** @type {CustomEvent} */ (e).detail) || {};
      const characterId = parseInt(detail.characterId || '0', 10);
      const isPrivate = detail.isPrivate === true;
      const nameHint = detail.characterName || '';
      try {
        const comp = await waitForComponent();
        if (comp) await runOpenCharacter(comp, characterId, isPrivate, nameHint);
      } catch (error) {
        console.error('[DeepLink] Failed to open character:', error);
      }
    });
  } catch (error) {
    console.error('Error initializing rankings page:', error);
    const errorContainer = document.getElementById('error-container');
    if (errorContainer) {
      errorContainer.innerHTML = `
        <div class="error-message">
          <h3>Something went wrong</h3>
          <p>${error.message}</p>
          <button id="retry-button">Retry</button>
        </div>
      `;

      const retryButton = document.getElementById('retry-button');
      if (retryButton) {
        retryButton.addEventListener('click', () => {
          location.reload();
        });
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', initRankingsPage);
